import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import AdminLayout from '../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Edit, Power, Settings } from 'lucide-react';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOOKUP_TYPES = [
  { key: 'property_types', label: 'Property Types' },
  { key: 'tenures', label: 'Tenures' },
  { key: 'construction_types', label: 'Construction Types' },
  { key: 'building_ages', label: 'Building Ages' },
  { key: 'energy_ratings', label: 'Energy Ratings' },
  { key: 'occupancy_statuses', label: 'Occupancy Statuses' },
  { key: 'property_statuses', label: 'Property Statuses' },
  { key: 'usage_types', label: 'Usage Types' }
];

export default function AdminLookupsPage() {
  const { token, user } = useContext(AuthContext);
  const [selectedType, setSelectedType] = useState('property_types');
  const [lookups, setLookups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    label: '',
    category: '',
    sort_order: 0
  });

  useEffect(() => {
    if (selectedType) {
      fetchLookups();
    }
  }, [selectedType]);

  const fetchLookups = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/lookups/${selectedType}?include_inactive=true`
      );
      setLookups(response.data);
    } catch (error) {
      toast.error('Failed to load lookup data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingItem(null);
    setFormData({ label: '', category: '', sort_order: lookups.length + 1 });
    setShowDialog(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      label: item.label,
      category: item.category || '',
      sort_order: item.sort_order
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    try {
      if (editingItem) {
        // Update
        await axios.put(
          `${API}/lookups/${selectedType}/${editingItem.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Lookup updated successfully');
      } else {
        // Create
        await axios.post(
          `${API}/lookups/${selectedType}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Lookup created successfully');
      }
      setShowDialog(false);
      fetchLookups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleToggleActive = async (item) => {
    try {
      if (item.is_active) {
        await axios.delete(
          `${API}/lookups/${selectedType}/${item.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Lookup deactivated');
      } else {
        await axios.put(
          `${API}/lookups/${selectedType}/${item.id}`,
          { is_active: true },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Lookup activated');
      }
      fetchLookups();
    } catch (error) {
      toast.error('Operation failed');
    }
  };

  // Check if user is admin
  const isAdmin = ['director', 'admin'].includes(user?.role);

  if (!isAdmin) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Admin access required</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-lookups-page">
        <div>
          <h1 className="text-4xl font-bold text-primary mb-2" data-testid="admin-lookups-title">
            Dropdown Configuration
          </h1>
          <p className="text-muted-foreground">Manage dropdown options for property setup</p>
        </div>

        <div className="flex flex-col md:flex-row gap-4">
          <Card className="md:w-64">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Settings className="h-5 w-5 mr-2 text-accent" />
                Lookup Types
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {LOOKUP_TYPES.map((type) => (
                  <button
                    key={type.key}
                    onClick={() => setSelectedType(type.key)}
                    className={`w-full text-left px-4 py-2 rounded-lg transition-colors ${
                      selectedType === type.key
                        ? 'bg-primary text-white'
                        : 'bg-secondary hover:bg-secondary/80'
                    }`}
                    data-testid={`lookup-type-${type.key}`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="flex-1">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>
                  {LOOKUP_TYPES.find((t) => t.key === selectedType)?.label}
                </CardTitle>
                <Button onClick={handleCreate} data-testid="create-lookup-button">
                  <Plus className="h-4 w-4 mr-2" />
                  Add New
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">Loading...</div>
              ) : (
                <Table data-testid="lookups-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Label</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Sort Order</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lookups.map((item) => (
                      <motion.tr
                        key={item.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        data-testid={`lookup-row-${item.id}`}
                      >
                        <TableCell className="font-medium">{item.label}</TableCell>
                        <TableCell>{item.category || '-'}</TableCell>
                        <TableCell>{item.sort_order}</TableCell>
                        <TableCell>
                          <Badge variant={item.is_active ? 'default' : 'secondary'}>
                            {item.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEdit(item)}
                              data-testid={`edit-lookup-${item.id}`}
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant={item.is_active ? 'destructive' : 'default'}
                              onClick={() => handleToggleActive(item)}
                              data-testid={`toggle-lookup-${item.id}`}
                            >
                              <Power className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </motion.tr>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent data-testid="lookup-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? 'Edit Lookup' : 'Create New Lookup'}
            </DialogTitle>
            <DialogDescription>
              {editingItem ? 'Update the lookup details' : 'Add a new option to the dropdown'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="label">Label *</Label>
              <Input
                id="label"
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                placeholder="Enter label"
                data-testid="lookup-label-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="category">Category (Optional)</Label>
              <Input
                id="category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                placeholder="e.g., residential, commercial"
                data-testid="lookup-category-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sort_order">Sort Order</Label>
              <Input
                id="sort_order"
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) })}
                data-testid="lookup-sort-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!formData.label} data-testid="save-lookup-button">
              {editingItem ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
